import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';
import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.kiera.terminal');
import { OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Service } from 'src/libs/portal/season/service';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';

function createSpringSocket(path: string) {
    const handlers: { [event: string]: Array<(data?: any) => void> } = {};
    let pending: string[] = [];
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${window.location.host}${path}`);
    const dispatch = (event: string, data: any) => {
        for (const handler of handlers[event] || []) handler(data);
    };
    socket.onopen = () => {
        for (const message of pending.splice(0)) socket.send(message);
        dispatch('connect', {});
    };
    socket.onmessage = (message: MessageEvent) => {
        try {
            const envelope = JSON.parse(String(message.data || '{}'));
            if (envelope?.event) dispatch(String(envelope.event), envelope.data || {});
        } catch (error) {
            dispatch('connect_error', { message: 'Invalid Spring WebSocket response' });
        }
    };
    socket.onerror = () => dispatch('connect_error', { message: 'Spring WebSocket connection failed' });
    socket.onclose = () => dispatch('disconnect', {});
    return {
        on: (event: string, handler: (data?: any) => void) => {
            if (!handlers[event]) handlers[event] = [];
            handlers[event].push(handler);
        },
        emit: (event: string, data: any = {}) => {
            const message = JSON.stringify({ event, data });
            if (socket.readyState === WebSocket.OPEN) socket.send(message);
            else if (socket.readyState === WebSocket.CONNECTING) pending.push(message);
        },
        close: () => {
            pending = [];
            if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) socket.close();
        },
    };
}

@Component({
 standalone: false,
 selector: 'wiz-page-kiera-terminal',
template: templateSource || '',
    styles: [`

/* file: /opt/app/project/main/.kiera-build/src/app/page.kiera.terminal/view.scss */
:host {
  display: block;
  height: 100%;
}`],
})
export class PageKieraTerminalComponent implements OnInit, OnDestroy {
    @ViewChild('terminalContainer') terminalContainerEl: ElementRef;

    public nodeId: number = 0;
    public nodeName: string = '';
    public nodeIp: string = '';
    public sshUser: string = 'root';
    public terminalAvailable: boolean = false;
    public terminalSocket: any = null;
    public terminalTerm: any = null;
    public terminalFit: any = null;
    public terminalConnected: boolean = false;
    public terminalConnecting: boolean = false;
    public terminalStatus: string = 'Disconnected';
    public terminalError: string = '';
    private terminalRoom: string = '';
    private resizeObserver: any = null;
    private terminalInputDisposable: any = null;
    private manualDisconnect: boolean = false;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        if (this.service.auth.allow('/access') === false) return;

        this.nodeId = parseInt(WizRoute.segment.nodeId) || 0;
        this.nodeName = new URLSearchParams(window.location.search).get('name') || `Node #${this.nodeId}`;
        document.title = `Terminal - ${this.nodeName}`;

        await this.loadInfo();
        await this.service.render();

        setTimeout(() => {
            this.initTerminal();
            if (this.canConnectTerminal()) this.connectTerminal();
        }, 200);
    }

    ngOnDestroy() {
        this.disconnectTerminal(false);
        this.disposeTerminal();
    }

    private async loadInfo() {
        if (!this.nodeId) {
            this.terminalError = 'Node ID is missing.';
            this.terminalAvailable = false;
            return;
        }
        const { code, data } = await wiz.call('info', { node_id: this.nodeId });
        if (code !== 200) {
            this.terminalError = data?.message || 'Failed to load node terminal info.';
            this.terminalAvailable = false;
            return;
        }
        this.nodeName = data.name || this.nodeName;
        this.nodeIp = data.ip_address || '';
        this.sshUser = data.default_login_user || this.sshUser || 'root';
        this.terminalAvailable = Boolean(data.terminal_available && this.nodeIp);
        if (!this.terminalAvailable) {
            this.terminalStatus = 'Unavailable';
        }
        document.title = `Terminal - ${this.nodeName}`;
    }

    public canConnectTerminal() {
        return Boolean(this.nodeId && this.nodeIp && this.terminalAvailable && this.sshUser && !this.terminalConnected && !this.terminalConnecting);
    }

    private initTerminal() {
        if (!this.terminalContainerEl || !this.terminalContainerEl.nativeElement) return;
        if (this.terminalTerm) return;

        const el = this.terminalContainerEl.nativeElement;
        const term = new Terminal({
            cursorBlink: true,
            convertEol: true,
            fontSize: 14,
            fontFamily: "'Fira Code', 'Cascadia Code', Menlo, monospace",
            scrollback: 10000,
            theme: {
                background: '#09090b',
                foreground: '#e4e4e7',
                cursor: '#bae6fd',
                selectionBackground: '#334155',
            }
        });
        const fit = new FitAddon();
        term.loadAddon(new WebLinksAddon());
        term.loadAddon(fit);
        term.open(el);
        fit.fit();

        this.terminalTerm = term;
        this.terminalFit = fit;
        this.writeStatus(`KiERA Terminal - ${this.nodeName}`);
        if (this.nodeIp) this.writeStatus(`Target ${this.nodeIp}`);

        this.resizeObserver = new ResizeObserver(() => {
            if (!this.terminalFit || !this.terminalTerm) return;
            this.terminalFit.fit();
            if (this.terminalConnected && this.terminalSocket) {
                this.terminalSocket.emit('terminal_resize', {
                    cols: this.terminalTerm.cols,
                    rows: this.terminalTerm.rows,
                });
            }
        });
        this.resizeObserver.observe(el);
    }

    private writeStatus(message: string, color: string = '36') {
        if (!this.terminalTerm) return;
        this.terminalTerm.writeln(`\x1b[${color}m[terminal]\x1b[0m ${message}`);
    }

    public async connectTerminal() {
        if (!this.canConnectTerminal()) {
            await this.service.render();
            return;
        }
        this.initTerminal();
        this.disconnectSocketOnly();
        this.terminalConnecting = true;
        this.terminalConnected = false;
        this.terminalError = '';
        this.terminalStatus = 'Connecting';
        this.manualDisconnect = false;
        await this.service.render();

        const randomStr = (len: number) => {
            const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
            let result = '';
            for (let i = 0; i < len; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
            return result;
        };
        this.terminalRoom = `terminal_${this.nodeId}_${randomStr(8)}`;
        const socket = createSpringSocket('/ws/terminal');
        this.terminalSocket = socket;

        socket.on('connect', () => {
            socket.emit('join', { room: this.terminalRoom });
            if (this.terminalFit) this.terminalFit.fit();
            const dims = this.terminalTerm ? { cols: this.terminalTerm.cols, rows: this.terminalTerm.rows } : { cols: 80, rows: 24 };
            this.writeStatus(`Connecting to ${this.sshUser}@${this.nodeIp} ...`);
            socket.emit('terminal_connect', {
                node_id: this.nodeId,
                room: this.terminalRoom,
                ssh_user: this.sshUser,
                ...dims,
            });
        });

        socket.on('connect_error', async (error: any) => {
            this.terminalConnecting = false;
            this.terminalConnected = false;
            this.terminalError = error?.message || 'Socket connection failed';
            this.terminalStatus = 'Error';
            this.writeStatus(this.terminalError, '31');
            await this.service.render();
        });

        socket.on('terminal_connected', async (data: any) => {
            this.terminalConnecting = false;
            this.terminalConnected = true;
            this.terminalStatus = 'Connected';
            if (data?.ip_address) this.nodeIp = data.ip_address;
            this.writeStatus(data?.message || 'SSH session connected', '32');
            await this.service.render();
        });

        socket.on('terminal_output', (data: any) => {
            if (this.terminalTerm && data?.output) {
                this.terminalTerm.write(String(data.output));
            }
        });

        socket.on('terminal_error', async (data: any) => {
            this.terminalConnecting = false;
            this.terminalConnected = false;
            this.terminalError = data?.message || 'Terminal connection failed';
            this.terminalStatus = 'Error';
            this.writeStatus(this.terminalError, '31');
            await this.service.render();
        });

        socket.on('terminal_exit', async (data: any) => {
            this.terminalConnecting = false;
            this.terminalConnected = false;
            this.terminalStatus = 'Disconnected';
            this.writeStatus(data?.message || 'SSH session ended', '33');
            await this.service.render();
        });

        socket.on('terminal_disconnected', async (data: any) => {
            this.terminalConnecting = false;
            this.terminalConnected = false;
            this.terminalStatus = 'Disconnected';
            this.writeStatus(data?.message || 'Disconnected', '33');
            await this.service.render();
        });

        socket.on('disconnect', async () => {
            this.terminalConnecting = false;
            this.terminalConnected = false;
            this.terminalStatus = 'Disconnected';
            if (!this.manualDisconnect) this.writeStatus('Socket disconnected.', '33');
            await this.service.render();
        });

        if (this.terminalInputDisposable) this.terminalInputDisposable.dispose();
        if (this.terminalTerm) {
            this.terminalInputDisposable = this.terminalTerm.onData((data: string) => {
                if (this.terminalConnected && this.terminalSocket) {
                    this.terminalSocket.emit('terminal_input', { input: data });
                }
            });
        }
    }

    public disconnectTerminal(render: boolean = true) {
        this.manualDisconnect = true;
        this.disconnectSocketOnly();
        this.terminalConnected = false;
        this.terminalConnecting = false;
        this.terminalStatus = 'Disconnected';
        if (render) void this.service.render();
    }

    private disconnectSocketOnly() {
        if (!this.terminalSocket) return;
        try {
            this.terminalSocket.emit('terminal_disconnect', {});
            this.terminalSocket.close();
        } catch (e) {
            console.warn('terminal socket cleanup failed', e);
        }
        this.terminalSocket = null;
    }

    public clearTerminal() {
        if (!this.terminalTerm) return;
        this.terminalTerm.clear();
        this.writeStatus(`KiERA Terminal - ${this.nodeName}`);
    }

    private disposeTerminal() {
        if (this.terminalInputDisposable) {
            this.terminalInputDisposable.dispose();
            this.terminalInputDisposable = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.terminalTerm) {
            this.terminalTerm.dispose();
            this.terminalTerm = null;
            this.terminalFit = null;
        }
    }
}

export default PageKieraTerminalComponent;
